export const STATUS_LABEL: Record<string, string> = {
  REQUESTED:      '결제대기',
  PAID:           '결제완료',
  SHIPPED:        '발송완료',
  CANCELLED:      '취소',
  FAIL:           '결제실패',
  CANCEL_FAIL:    '취소실패',
  HASH_MISMATCH:  '검증실패',
  USER_CANCELLED: '고객취소',
};

/**
 * Guarded order-status transition matrix. A transition is legal only if `to`
 * appears in `ALLOWED_TRANSITIONS[from]` (or `from === to`, an idempotent re-write).
 *
 * NOTE: `USER_CANCELLED -> PAID` is deliberately NOT here. The late-authoritative-PAID
 * recovery (see `recoverLateAuthoritativePaid` in lib/stockStore.ts) is the ONLY sanctioned
 * `USER_CANCELLED -> PAID` writer, gated under `FOR UPDATE` by both `status` and `refunded`.
 * Widening the matrix would reopen the "stray late pay-complete resurrects a user-cancelled
 * order" hole.
 *
 * `CANCEL_FAIL: []` makes `CANCEL_FAIL -> CANCELLED` illegal; a successful cancel-retry via a
 * second async noti after a prior failed cancel is rejected — this fails SAFE toward no-oversell
 * (unit held, not re-sold) and keeps `stock == SUM(delta)` intact. Known low-severity residual.
 */
export const ALLOWED_TRANSITIONS: Record<string, string[]> = {
  REQUESTED:      ['PAID', 'FAIL', 'HASH_MISMATCH', 'CANCELLED', 'USER_CANCELLED'],
  PAID:           ['CANCELLED', 'CANCEL_FAIL', 'SHIPPED'],
  SHIPPED:        [],
  FAIL:           [],
  HASH_MISMATCH:  [],
  CANCELLED:      [],
  USER_CANCELLED: [],
  CANCEL_FAIL:    [],
};

/**
 * Terminal statuses whose RESERVATION-basis stock (`products.stock`) is eligible for release.
 * Excludes PAID, CANCEL_FAIL, and SHIPPED — a shipped order's reservation was already permanently
 * consumed; shipping only affects the separate physical `real_stock` ledger, never releases `stock`.
 */
export const RELEASE_ELIGIBLE = new Set(['FAIL', 'HASH_MISMATCH', 'CANCELLED', 'USER_CANCELLED']);

export function isLegalTransition(from: string, to: string): boolean {
  if (from === to) return true; // idempotent re-write
  return (ALLOWED_TRANSITIONS[from] ?? []).includes(to);
}

export function statusLabel(status: string | null | undefined): string {
  if (!status) return '-';
  return STATUS_LABEL[status] ?? status;
}

export function formatOrderNo(orderNo: string): string {
  // 신규 형식(YYMMDD-HHMMSS-랜덤6자리)은 원본 그대로가 곧 표시 형식
  if (/^\d{6}-\d{6}-\d{6}$/.test(orderNo)) return orderNo;

  // 레거시 형식(order_YYYYMMDDHHMMSS_hex6) 하위호환
  const parts = orderNo.split('_');
  const suffix = parts.at(-1) ?? '';
  const ts = parts.at(-2) ?? '';
  const date = ts.slice(0, 6);
  const time = ts.slice(6, 12);
  return [date, time, suffix].filter(Boolean).join('-');
}

export function normalizeOrderNo(input: string): string {
  return input.trim();
}
